import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { formatMoney } from "../../../../../shared/utils/formatMoney";
import { calculateLineTotals } from "../utils/purchaseModel";

const baseColumns = [
  { key: "item", label: "Ítem", width: "minmax(145px, 1.5fr)", align: "left" },
  { key: "presentation", label: "Presentación", width: "minmax(140px, 1.35fr)", align: "left" },
  { key: "requested", label: "Pedido", width: "minmax(82px, .7fr)", align: "center" },
  { key: "currentCost", label: "Precio vigente", width: "minmax(105px, .85fr)", align: "center" },
  { key: "received", label: "Ingreso", width: "minmax(82px, .7fr)", align: "center" },
  { key: "newCost", label: "Precio nuevo", width: "minmax(105px, .85fr)", align: "center" },
];

const totalColumns = [
  { key: "requestedTotal", label: "Total pedido", width: "minmax(108px, .9fr)", align: "center" },
  { key: "receivedTotal", label: "Total compra", width: "minmax(108px, .9fr)", align: "center" },
];

const actionColumn = {
  key: "action",
  label: "",
  width: "minmax(44px, .35fr)",
  align: "center",
};

function gridSx(columns) {
  return {
  display: "grid",
  gridTemplateColumns: columns.map((column) => column.width).join(" "),
  columnGap: 0.75,
  alignItems: "center",
  "& > *": { minWidth: 0 },
  };
}

const UNIT_LABELS = {
  g: "g",
  ml: "ml",
  unit: "unidad",
  package: "paquete",
};

function presentationLabel(line) {
  return (
    line.presentation ||
    UNIT_LABELS[line.purchaseUnit] ||
    line.purchaseUnit ||
    "—"
  );
}

function itemLabel(line) {
  const ingredientName = line.ingredientName || "Sin nombre";
  return line.brand ? `${ingredientName} · ${line.brand}` : ingredientName;
}

function TextCell({ children, muted = false, align = "left" }) {
  return (
    <Typography
      noWrap
      title={typeof children === "string" ? children : undefined}
      sx={{
        color: muted ? "neutral.600" : "primary.900",
        fontSize: 12.5,
        fontWeight: 700,
        textAlign: align,
      }}
    >
      {children}
    </Typography>
  );
}

function EditableField({ label, value, error, inputMode, disabled, onChange }) {
  return (
    <TextField
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      error={Boolean(error)}
      helperText={error || " "}
      size="small"
      inputProps={{ inputMode, maxLength: 14, "aria-label": label }}
      sx={{
        my: -0.65,
        "& .MuiOutlinedInput-root": {
          height: 34,
          borderRadius: "12px",
          bgcolor: "neutral.50",
          fontSize: 12.5,
          fontWeight: 700,
          "& fieldset": { borderColor: error ? "error.main" : "rgba(96,96,113,.28)" },
        },
        "& .MuiOutlinedInput-input": { textAlign: "center", px: 1.25 },
        "& .MuiFormHelperText-root": {
          mx: 0.25,
          mt: 0.2,
          minHeight: 11,
          fontSize: 9,
          lineHeight: 1.1,
          fontWeight: 650,
        },
      }}
    />
  );
}

export default function PurchaseLinesTable({
  rows,
  loading,
  readOnly,
  errors,
  onFieldChange,
  onAdd,
  onRemove,
  emptyMessage,
  showTotals = true,
}) {
  const columns = showTotals
    ? [...baseColumns, ...totalColumns, actionColumn]
    : [...baseColumns, actionColumn];

  return (
    <Box sx={{ position: "relative", flex: 1, minHeight: 0, borderRadius: "20px", overflow: "hidden" }}>
      <Box
        sx={{
          height: "100%",
          overflow: "auto",
          scrollbarGutter: showTotals ? "stable" : "auto",
        }}
      >
        <Box
          sx={{
            minWidth: showTotals ? 1040 : 760,
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              ...gridSx(columns),
              flexShrink: 0,
              px: 2,
              py: 1.35,
              bgcolor: "neutral.500",
              borderRadius: "20px 20px 0 0",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            {columns.map((column) => (
              <Typography
                key={column.key}
                sx={{
                  color: "primary.900",
                  fontSize: 13,
                  fontWeight: 800,
                  textAlign: column.align,
                }}
              >
                {column.label}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              flex: 1,
              bgcolor: "neutral.50",
            }}
          >
            {rows.length ? (
              rows.map((line) => {
                const totals = calculateLineTotals(line);
                const lineErrors = errors[line.purchaseItemId] || {};
                const canAdd = !line.isSelected && !readOnly;
                const canRemove = line.isSelected && !readOnly;
                const addLine = () => canAdd && onAdd(line);

                return (
                  <Box
                    key={line.purchaseItemId}
                    role={canAdd ? "button" : undefined}
                    tabIndex={canAdd ? 0 : undefined}
                    aria-label={canAdd ? `Agregar fila de ${line.ingredientName}` : undefined}
                    onClick={addLine}
                    onKeyDown={(event) => {
                      if (canAdd && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault();
                        addLine();
                      }
                    }}
                    sx={{
                      ...gridSx(columns),
                      minHeight: 58,
                      px: 2,
                      py: 0.8,
                      opacity: line.isSelected ? 1 : 0.28,
                      borderBottom: "1px solid rgba(0,0,0,.1)",
                      cursor: canAdd ? "pointer" : "default",
                      transition: "opacity .15s, background-color .15s",
                      "&:hover": { bgcolor: canAdd ? "secondary.50" : "rgba(0,0,0,.02)" },
                      "&:focus-visible": { outline: "2px solid", outlineColor: "secondary.500", outlineOffset: -2 },
                    }}
                  >
                    <TextCell>{itemLabel(line)}</TextCell>
                    <TextCell>{presentationLabel(line)}</TextCell>

                    {line.isSelected && !readOnly ? (
                      <EditableField
                        label={`Pedido de ${line.ingredientName}`}
                        value={line.requestedQuantity}
                        error={lineErrors.requestedQuantity}
                        inputMode="numeric"
                        onChange={(value) => onFieldChange(line.purchaseItemId, "requestedQuantity", value)}
                      />
                    ) : (
                      <TextCell muted={!line.isSelected} align="center">
                        {line.isSelected ? line.requestedQuantity : "—"}
                      </TextCell>
                    )}

                    <TextCell align="center">
                      {formatMoney(line.currentUnitCostSnapshot)}
                    </TextCell>

                    {line.isSelected && !readOnly ? (
                      <EditableField
                        label={`Ingreso de ${line.ingredientName}`}
                        value={line.receivedQuantity}
                        error={lineErrors.receivedQuantity}
                        inputMode="numeric"
                        onChange={(value) => onFieldChange(line.purchaseItemId, "receivedQuantity", value)}
                      />
                    ) : (
                      <TextCell muted={!line.isSelected} align="center">
                        {line.isSelected
                          ? line.receivedQuantity === ""
                            ? "—"
                            : line.receivedQuantity
                          : "—"}
                      </TextCell>
                    )}

                    {line.isSelected && !readOnly ? (
                      <EditableField
                        label={`Precio nuevo de ${line.ingredientName}`}
                        value={line.newUnitCost}
                        error={lineErrors.newUnitCost}
                        inputMode="decimal"
                        onChange={(value) => onFieldChange(line.purchaseItemId, "newUnitCost", value)}
                      />
                    ) : (
                      <TextCell muted={!line.isSelected} align="center">
                        {line.isSelected && line.newUnitCost !== ""
                          ? formatMoney(line.newUnitCost)
                          : "—"}
                      </TextCell>
                    )}

                    {showTotals ? (
                      <>
                        <TextCell muted={!line.isSelected} align="center">
                          {line.isSelected ? formatMoney(totals.requestedTotal, "—") : "—"}
                        </TextCell>
                        <TextCell muted={!line.isSelected} align="center">
                          {line.isSelected ? formatMoney(totals.receivedTotal, "") : "—"}
                        </TextCell>
                      </>
                    ) : null}

                    <Stack sx={{ alignItems: "center" }}>
                      {canAdd || canRemove ? (
                        <Tooltip title={canAdd ? "Agregar ítem" : "Quitar ítem"} arrow>
                          <IconButton
                            type="button"
                            size="small"
                            aria-label={`${canAdd ? "Agregar" : "Quitar"} ${line.ingredientName}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (canAdd) onAdd(line);
                              else onRemove(line.purchaseItemId);
                            }}
                            sx={{
                              width: 23,
                              height: 23,
                              borderRadius: "6px",
                              bgcolor: canAdd
                                ? "secondary.200"
                                : "rgba(244, 181, 181, .72)",
                              color: canAdd ? "primary.700" : "error.dark",
                              "&:hover": {
                                bgcolor: canAdd
                                  ? "secondary.300"
                                  : "rgba(238, 151, 151, .86)",
                              },
                            }}
                          >
                            {canAdd ? <AddIcon fontSize="small" /> : <RemoveIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ color: "neutral.600", fontWeight: 800 }}>—</Typography>
                      )}
                    </Stack>
                  </Box>
                );
              })
            ) : (
              <Stack sx={{ height: "100%", alignItems: "center", justifyContent: "center", p: 3 }}>
                <Typography sx={{ color: "primary.900", fontSize: 14, fontWeight: 700 }}>
                  {emptyMessage}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box
          aria-label="Cargando ítems del proveedor"
          sx={{
            position: "absolute",
            top: 8,
            right: 12,
            width: 28,
            height: 28,
            display: "grid",
            placeItems: "center",
            borderRadius: "50%",
            bgcolor: "rgba(247,247,248,.9)",
          }}
        >
          <CircularProgress size={17} />
        </Box>
      ) : null}
    </Box>
  );
}
